// Análise financeira do cancelamento (#5829, projeto 11).
//
// O checklist de `validacaoCancelamento.ts` confere cadastro, contrato e
// duplicidade. Aqui ficam os três números que o Vinicius Alves pediu na
// análise: faturas em aberto, saldo remanescente e multa.
//
// Nada aqui bloqueia a validação (decisão de 17/09/2026): o bloco informa, e
// quem bloqueia continua sendo o checklist de cadastro.
//
// As regras de multa, carência e fidelidade vêm do Termo de Adesão anexado na
// demanda 01 (#5825), lido em 24/09/2026:
//   • 13.4  — multa = PERCENTUAL_MULTA do valor base mensal × meses vincendos.
//             Base: média das 3 últimas Contribuições PAGAS (I); sem 3 pagas,
//             média do período (II); sem nenhuma, estimativa (13.11-13.13).
//   • 13.7  — carência conta do primeiro registro/compensação de créditos; nela
//             a saída é SEM multa, mas o saldo continua devido.
//   • 13.8  — fidelidade conta da PRIMEIRA COMPENSAÇÃO na fatura, não da
//             assinatura do termo.
//   • Quadro Resumo — aviso prévio mínimo de 90 dias para a saída.
//
// Sem I/O e sem Date.now(): "hoje" e os dados entram por parâmetro.

export type EstadoCalculo = 'calculado' | 'indisponivel';

export interface ValorCalculado<T> {
  estado: EstadoCalculo;
  valor: T | null;
  /** Por que não deu para calcular. Nunca mostrar "R$ 0,00" no lugar disso. */
  motivo: string | null;
}

const ok = <T>(valor: T): ValorCalculado<T> => ({ estado: 'calculado', valor, motivo: null });
const falta = <T>(motivo: string): ValorCalculado<T> => ({ estado: 'indisponivel', valor: null, motivo });

/** yyyy-mm-dd → dd/mm/yyyy, para nunca vazar data ISO num texto lido por gente. */
const dataBr = (iso: string): string => iso.split('-').reverse().join('/');

const num = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

/** Contribuições pagas que formam a base da multa (cláusula 13.4-I). */
export const CONTRIBUICOES_NA_BASE = 3;

/** Percentual máximo da multa sobre o valor base mensal (cláusula 13.4). */
export const PERCENTUAL_MULTA = 0.30;

/** Aviso prévio mínimo de saída, em dias corridos (Quadro Resumo do Termo). */
export const AVISO_PREVIO_DIAS = 90;

/** De onde saiu o valor base mensal — a cláusula exige demonstrativo do cálculo. */
export type OrigemBaseMensal = 'tres_ultimas_pagas' | 'media_do_periodo';

export interface BaseMensal {
  valor: number;
  origem: OrigemBaseMensal;
  /** Quantas contribuições entraram na conta. */
  consideradas: number;
}

const medias = (v: number[]) => Math.round((v.reduce((s, x) => s + x, 0) / v.length) * 100) / 100;

/** "1 contribuição" / "2 contribuições" — concordância na frase que o analista lê. */
export const plural = (n: number, singular: string, pluralForma: string) =>
  `${n} ${n === 1 ? singular : pluralForma}`;

/**
 * Valor base mensal da multa (cláusula 13.4):
 *   I  — com 3 ou mais contribuições PAGAS, a média das 3 últimas;
 *   II — sem 3 pagas, a média das contribuições desde o início da vigência.
 *
 * Sem nenhuma contribuição paga, a cláusula 13.11-13.13 manda estimar a partir
 * do consumo da distribuidora e dos parâmetros do plano — conta que depende de
 * tarifa e de desconto do plano e que o sistema não faz sozinho hoje. Nesse
 * caso devolvemos indisponível com o motivo, em vez de inventar um número que
 * viraria cobrança.
 *
 * Ambas as listas vêm do banco ordenadas da mais recente para a mais antiga.
 */
export function valorBaseMensal(
  pagasMaisRecentesPrimeiro: Array<number | string | null | undefined>,
  todasMaisRecentesPrimeiro: Array<number | string | null | undefined>,
): ValorCalculado<BaseMensal> {
  const positivos = (lista: Array<number | string | null | undefined>) =>
    (lista || []).map(num).filter((v): v is number => v !== null && v > 0);

  const pagas = positivos(pagasMaisRecentesPrimeiro);
  if (pagas.length >= CONTRIBUICOES_NA_BASE) {
    const ultimas = pagas.slice(0, CONTRIBUICOES_NA_BASE);
    return ok({ valor: medias(ultimas), origem: 'tres_ultimas_pagas', consideradas: ultimas.length });
  }
  const todas = positivos(todasMaisRecentesPrimeiro);
  if (todas.length > 0) {
    return ok({ valor: medias(todas), origem: 'media_do_periodo', consideradas: todas.length });
  }
  return falta('a UC não tem contribuição com valor para formar a base da multa — a cláusula 13.13 pede estimativa manual pelo consumo');
}

export interface SaldoRemanescente {
  saldoKwh: number;
  /** Saldo <= 0: não há energia a usar, e a conta de meses vira zero. */
  temSaldo: boolean;
  mesesDeConsumo: number | null;
}

/**
 * Saldo em kWh e quantos meses de consumo ele cobre. Saldo negativo aparece com
 * o número (decisão de 17/09/2026) e conta como zero mês. Sem consumo médio a
 * tela mostra só o saldo, sem estimar.
 */
export function saldoRemanescente(
  saldoKwh: number | string | null | undefined,
  consumoMedioKwh: number | string | null | undefined,
): ValorCalculado<SaldoRemanescente> {
  const saldo = num(saldoKwh);
  if (saldo === null) return falta('a UC não tem fatura recente para ler o saldo');
  const consumo = num(consumoMedioKwh);
  if (saldo <= 0) return ok({ saldoKwh: saldo, temSaldo: false, mesesDeConsumo: 0 });
  if (consumo === null || consumo <= 0) return ok({ saldoKwh: saldo, temSaldo: true, mesesDeConsumo: null });
  return ok({ saldoKwh: saldo, temSaldo: true, mesesDeConsumo: Math.round((saldo / consumo) * 10) / 10 });
}

export interface Fidelidade {
  mesesTotal: number;
  mesesCumpridos: number;
  mesesRestantes: number;
  dentroDaFidelidade: boolean;
  fimDaFidelidade: string;
}

/** "12 meses" → 12. "Sem fidelidade" → 0. Qualquer outro texto → null. */
export function mesesDeFidelidade(texto: string | null | undefined): number | null {
  const t = String(texto ?? '').trim().toLowerCase();
  if (!t) return null;
  if (t.startsWith('sem ')) return 0;
  const m = t.match(/^(\d{1,2})\s*(mes|meses|m)?$/);
  return m ? Number(m[1]) : null;
}

const soData = (v: string | null | undefined): string | null => {
  const t = String(v ?? '').trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : null;
};

/** Meses inteiros completos entre duas datas ISO (o dia do mês conta). */
export function mesesCompletosEntre(de: string, ate: string): number {
  const [a1, m1, d1] = de.split('-').map(Number);
  const [a2, m2, d2] = ate.split('-').map(Number);
  let meses = (a2 - a1) * 12 + (m2 - m1);
  if (d2 < d1) meses -= 1;
  return meses;
}

export function fidelidadeDoContrato(
  fidelidadeTexto: string | null | undefined,
  /** Cláusula 13.8: a primeira compensação de créditos. Sem ela, a assinatura do termo. */
  dataInicio: string | null | undefined,
  hoje: string,
): ValorCalculado<Fidelidade> {
  const meses = mesesDeFidelidade(fidelidadeTexto);
  if (meses === null) {
    return falta(String(fidelidadeTexto ?? '').trim()
      ? `a fidelidade do contrato está como "${String(fidelidadeTexto).trim()}", que não dá para interpretar`
      : 'o contrato não tem fidelidade registrada');
  }
  const assinatura = soData(dataInicio);
  const dia = soData(hoje);
  if (!assinatura || !dia) return falta('não há data de início da fidelidade (nem primeira compensação, nem assinatura do termo)');
  if (meses === 0) {
    return ok({ mesesTotal: 0, mesesCumpridos: 0, mesesRestantes: 0, dentroDaFidelidade: false, fimDaFidelidade: assinatura });
  }
  const cumpridos = Math.max(0, mesesCompletosEntre(assinatura, dia));
  const restantes = Math.max(0, meses - cumpridos);
  // Dia 31 + 1 mês cairia em 03/03 (fevereiro não tem 31) e a tela mostraria a
  // fidelidade terminando DEPOIS do fim do mês. Trava no último dia do mês.
  const [ano, mes, d] = assinatura.split('-').map(Number);
  const ultimoDiaDoMesAlvo = new Date(Date.UTC(ano, mes + meses, 0)).getUTCDate();
  const fim = new Date(Date.UTC(ano, mes - 1 + meses, Math.min(d, ultimoDiaDoMesAlvo)));
  return ok({
    mesesTotal: meses,
    mesesCumpridos: cumpridos,
    mesesRestantes: restantes,
    dentroDaFidelidade: restantes > 0,
    fimDaFidelidade: fim.toISOString().slice(0, 10),
  });
}

export interface Carencia {
  mesesTotal: number;
  fimDaCarencia: string;
  dentroDaCarencia: boolean;
}

/**
 * Carência (cláusula 13.7): conta do primeiro registro/compensação de créditos.
 * Dentro dela a saída é SEM multa — o saldo e os valores vencidos continuam
 * devidos, e isso é tratado nas outras linhas da análise.
 */
export function carenciaDoContrato(
  dataInicio: string | null | undefined,
  mesesCarencia: number | null | undefined,
  hoje: string,
): ValorCalculado<Carencia> {
  const meses = num(mesesCarencia);
  if (meses === null || meses <= 0) return falta('o plano não tem carência configurada');
  const inicio = soData(dataInicio);
  const dia = soData(hoje);
  if (!inicio || !dia) return falta('não há data de início da carência (primeira compensação de créditos)');

  const [ano, mes, d] = inicio.split('-').map(Number);
  const ultimoDia = new Date(Date.UTC(ano, mes + meses, 0)).getUTCDate();
  const fim = new Date(Date.UTC(ano, mes - 1 + meses, Math.min(d, ultimoDia))).toISOString().slice(0, 10);
  return ok({ mesesTotal: meses, fimDaCarencia: fim, dentroDaCarencia: dia <= fim });
}

export interface Multa {
  valor: number;
  baseMensal: number;
  origemBase: OrigemBaseMensal;
  mesesVincendos: number;
  percentual: number;
  /** Demonstrativo básico de cálculo — a cláusula 13.4-III exige mandar junto da cobrança. */
  demonstrativo: string;
}

/**
 * Multa compensatória (cláusula 13.4): percentual × valor base mensal × meses
 * vincendos da fidelidade. Não é devida na carência (13.7), fora da fidelidade,
 * nem quando o plano não tem fidelidade.
 */
export function calcularMulta(
  carencia: ValorCalculado<Carencia>,
  fidelidade: ValorCalculado<Fidelidade>,
  base: ValorCalculado<BaseMensal>,
  percentual: number = PERCENTUAL_MULTA,
): ValorCalculado<Multa> {
  if (carencia.valor?.dentroDaCarencia) {
    return falta(`sem multa: o cliente está na carência até ${dataBr(carencia.valor.fimDaCarencia)} (cláusula 13.7)`);
  }
  if (fidelidade.estado === 'indisponivel') {
    return falta(`não dá para calcular a multa porque ${fidelidade.motivo}`);
  }
  const f = fidelidade.valor!;
  if (f.mesesTotal === 0) return falta('sem multa: o contrato não tem fidelidade');
  if (!f.dentroDaFidelidade) return falta('sem multa: a fidelidade já foi cumprida');
  if (base.estado === 'indisponivel') {
    return falta(`não dá para calcular a multa porque ${base.motivo}`);
  }

  const b = base.valor!;
  const valor = Math.round(b.valor * percentual * f.mesesRestantes * 100) / 100;
  const brlLocal = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const comoBase = b.origem === 'tres_ultimas_pagas'
    ? `média das ${b.consideradas} últimas contribuições pagas`
    : `média de ${plural(b.consideradas, 'contribuição', 'contribuições')} do período (cláusula 13.4-II: não há 3 pagas)`;

  return ok({
    valor,
    baseMensal: b.valor,
    origemBase: b.origem,
    mesesVincendos: f.mesesRestantes,
    percentual,
    demonstrativo: `${Math.round(percentual * 100)}% de ${brlLocal(b.valor)} (${comoBase}) `
      + `× ${f.mesesRestantes} mês(es) restantes de fidelidade = ${brlLocal(valor)} (cláusula 13.4)`,
  });
}

export interface EntradaAnaliseFinanceira {
  faturasEmAbertoQtd: number;
  faturasEmAbertoValor: number;
  saldoAcumuladoKwh: number | null;
  /** Mês de referência da fatura de onde saiu o saldo, para a tela datar o número. */
  saldoMesReferencia: string | null;
  consumoMedioKwh: number | null;
  /** Todas as contribuições com valor, da mais recente para a mais antiga (cláusula 13.4-II). */
  valoresBoletosMaisRecentesPrimeiro: Array<number | string | null | undefined>;
  /** Só as contribuições PAGAS, da mais recente para a mais antiga (cláusula 13.4-I). */
  valoresBoletosPagosMaisRecentesPrimeiro: Array<number | string | null | undefined>;
  temContrato: boolean;
  fidelidadeTexto: string | null;
  dataAssinaturaTermo: string | null;
  /** Vencimento da primeira fatura com compensação — marco das cláusulas 13.7 e 13.8. */
  dataPrimeiraCompensacao: string | null;
  /** Meses de carência do plano (configuração; 3 pela resposta da demanda 01). */
  mesesCarencia: number | null;
  /** Percentual da multa (configuração; 30% pela cláusula 13.4). */
  percentualMulta?: number | null;
  /** Dias de aviso prévio (configuração; 90 pelo Quadro Resumo). */
  avisoPrevioDias?: number | null;
  hoje: string;
}

export interface AnaliseFinanceira {
  faturasEmAberto: { qtd: number; valor: number };
  saldo: ValorCalculado<SaldoRemanescente>;
  saldoMesReferencia: string | null;
  baseMensal: ValorCalculado<BaseMensal>;
  carencia: ValorCalculado<Carencia>;
  fidelidade: ValorCalculado<Fidelidade>;
  /** De onde saiu a data que iniciou fidelidade e carência — a tela mostra isso. */
  inicioContagem: { data: string | null; origem: 'primeira_compensacao' | 'assinatura_termo' | null };
  multa: ValorCalculado<Multa>;
  /** Dias de aviso prévio da saída (Quadro Resumo). */
  avisoPrevioDias: number;
}

/** Chave do item que carrega a análise dentro de `validacao_itens`. */
export const CHAVE_ITEM_ANALISE = 'analise_financeira';

/**
 * Relê a análise congelada na validação (`solicitacoes_cancelamento.validacao_itens`).
 * A tarefa 07 monta a mensagem a partir DAQUI, não recalculando: entre validar e
 * responder o cliente, saldo e faturas mudam, e o cliente tem que receber o que
 * o atendente viu. Validação antiga (antes de #5829) não tem o item e devolve null.
 */
export function lerAnaliseFinanceiraSalva(validacaoItens: unknown): AnaliseFinanceira | null {
  if (!Array.isArray(validacaoItens)) return null;
  const item = validacaoItens.find((i: any) => i && typeof i === 'object' && i.chave === CHAVE_ITEM_ANALISE);
  const dados = (item as any)?.dados;
  if (!dados || typeof dados !== 'object') return null;
  const a = dados as AnaliseFinanceira;
  // Formato novo (24/09/2026, regras do Termo). Validação salva antes disso não
  // tem `carencia`/`baseMensal` e quebraria a montagem da mensagem — devolver
  // null faz a tela pedir para revalidar, que é o comportamento já tratado.
  if (!a.faturasEmAberto || typeof a.faturasEmAberto.qtd !== 'number') return null;
  if (!a.multa || !a.carencia || !a.baseMensal || !a.inicioContagem) return null;
  return a;
}

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Uma linha de texto com o que a tela mostrou — é o que fica legível no histórico. */
export function resumoAnaliseFinanceira(a: AnaliseFinanceira): string {
  const partes: string[] = [
    a.faturasEmAberto.qtd > 0
      ? `${a.faturasEmAberto.qtd} fatura(s) em aberto somando ${brl(a.faturasEmAberto.valor)}`
      : 'sem faturas em aberto',
    a.saldo.valor
      ? (a.saldo.valor.temSaldo
        ? `saldo de ${Math.round(a.saldo.valor.saldoKwh).toLocaleString('pt-BR')} kWh`
          + (a.saldo.valor.mesesDeConsumo !== null ? ` (~${a.saldo.valor.mesesDeConsumo} mês(es))` : '')
        : `sem saldo (${Math.round(a.saldo.valor.saldoKwh).toLocaleString('pt-BR')} kWh)`)
      : `saldo não calculado — ${a.saldo.motivo}`,
    a.baseMensal.valor
      ? `base mensal de ${brl(a.baseMensal.valor.valor)} (${a.baseMensal.valor.origem === 'tres_ultimas_pagas'
        ? '3 últimas pagas' : `média de ${a.baseMensal.valor.consideradas} contribuições`})`
      : `base mensal não calculada — ${a.baseMensal.motivo}`,
    a.fidelidade.valor
      ? (a.fidelidade.valor.mesesTotal === 0
        ? 'contrato sem fidelidade'
        : a.fidelidade.valor.dentroDaFidelidade
          ? `fidelidade com ${a.fidelidade.valor.mesesRestantes} de ${a.fidelidade.valor.mesesTotal} meses restantes`
          : 'fidelidade já cumprida')
      : `fidelidade não calculada — ${a.fidelidade.motivo}`,
    a.multa.valor ? `multa de ${brl(a.multa.valor.valor)} (${a.multa.valor.demonstrativo})` : `multa: ${a.multa.motivo}`,
  ];
  return `${partes.join('; ')}.`;
}

export function montarAnaliseFinanceira(e: EntradaAnaliseFinanceira): AnaliseFinanceira {
  // Cláusulas 13.7 e 13.8: o marco é a primeira compensação. A assinatura do
  // termo é reserva para as UCs que ainda não têm fatura com crédito.
  const primeira = String(e.dataPrimeiraCompensacao ?? '').trim().slice(0, 10) || null;
  const assinatura = String(e.dataAssinaturaTermo ?? '').trim().slice(0, 10) || null;
  // Trava de sanidade: compensação ANTES da assinatura é dado torto (fatura
  // importada com economia errada, ou número de UC reaproveitado por outro
  // cliente — já aconteceu em produção). Nesses casos vale a assinatura, senão
  // a fidelidade começaria cedo demais e a multa sairia maior contra o cliente.
  const compensacaoValida = primeira && (!assinatura || primeira >= assinatura) ? primeira : null;
  const inicio = compensacaoValida || assinatura || primeira;
  const inicioContagem = {
    data: inicio,
    origem: (compensacaoValida ? 'primeira_compensacao' : (assinatura || primeira) ? 'assinatura_termo' : null) as
      'primeira_compensacao' | 'assinatura_termo' | null,
  };
  const percentualCfg = num(e.percentualMulta);
  const percentual = percentualCfg !== null && percentualCfg > 0 && percentualCfg <= 1
    ? percentualCfg
    : PERCENTUAL_MULTA;

  const fidelidade = e.temContrato
    ? fidelidadeDoContrato(e.fidelidadeTexto, inicio, e.hoje)
    : falta<Fidelidade>('a UC não tem contrato vinculado');
  const carencia = carenciaDoContrato(inicio, e.mesesCarencia, e.hoje);
  const baseMensal = valorBaseMensal(e.valoresBoletosPagosMaisRecentesPrimeiro, e.valoresBoletosMaisRecentesPrimeiro);

  return {
    faturasEmAberto: { qtd: Math.max(0, Math.trunc(e.faturasEmAbertoQtd || 0)), valor: num(e.faturasEmAbertoValor) ?? 0 },
    saldo: saldoRemanescente(e.saldoAcumuladoKwh, e.consumoMedioKwh),
    saldoMesReferencia: String(e.saldoMesReferencia ?? '').trim() || null,
    baseMensal,
    carencia,
    fidelidade,
    inicioContagem,
    multa: calcularMulta(carencia, fidelidade, baseMensal, percentual),
    avisoPrevioDias: num(e.avisoPrevioDias) ?? AVISO_PREVIO_DIAS,
  };
}
